import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import {
  ArrowLeft, Upload, FileText, Trash2, Phone, Mail, Globe, MessageCircle, IdCard, Pencil,
  GraduationCap, CheckCircle2, Circle,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_STYLES = {
  "New Lead": "bg-blue-50 text-blue-700 border-blue-200",
  "Documents Collected": "bg-amber-50 text-amber-700 border-amber-200",
  "University Applied": "bg-purple-50 text-purple-700 border-purple-200",
  "Accepted": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Visa Processing": "bg-orange-50 text-orange-700 border-orange-200",
  "Enrolled": "bg-teal-50 text-teal-700 border-teal-200",
};

function whatsappUrl(phone, message) {
  const digits = (phone || "").replace(/[^\d]/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

export default function StudentDetail() {
  const { id } = useParams();
  const { t, ts, tc, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [student, setStudent] = useState(null);
  const [notes, setNotes] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [apps, setApps] = useState([]);
  const [meta, setMeta] = useState({ stages: [], countries: [], application_statuses: [] });

  const load = async () => {
    const [s, n, d, tk, ap, m] = await Promise.all([
      api.get(`/students/${id}`),
      api.get(`/students/${id}/notes`),
      api.get(`/students/${id}/documents`),
      api.get(`/tasks`, { params: { student_id: id } }),
      api.get(`/students/${id}/applications`),
      api.get(`/meta`),
    ]);
    setStudent(s.data); setNotes(n.data); setDocs(d.data);
    setTasks(tk.data); setApps(ap.data); setMeta(m.data);
  };

  useEffect(() => { load().catch(() => {}); }, [id]);

  const updateStage = async (stage) => {
    try {
      const { data } = await api.patch(`/students/${id}`, { stage });
      setStudent(data);
      toast.success(t("stage"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteStudent = async () => {
    const warning = lang === "ar"
      ? `سيتم حذف "${student.name}" نهائياً مع كل الملاحظات والمهام والمستندات المرتبطة به.\n\nلا يمكن التراجع. متابعة؟`
      : `This will permanently delete "${student.name}" along with all linked notes, tasks and documents.\n\nThis cannot be undone. Continue?`;
    if (!window.confirm(warning)) return;

    setDeleting(true);
    try {
      await api.delete(`/students/${id}`);
      toast.success(lang === "ar" ? "تم حذف الطالب" : "Student deleted");
      navigate("/students");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setDeleting(false);
    }
  };

  const openWhatsApp = () => {
    if (!student) return;
    const msg = lang === "ar"
      ? `مرحباً ${student.name}، معك ${"MER"} للاستشارات التعليمية. أود متابعة ملفك الدراسي.`
      : `Hi ${student.name}, this is MER Educational Consultancy following up on your study-abroad application.`;
    const url = whatsappUrl(student.phone, msg);
    if (!url) {
      toast.error(lang === "ar" ? "لا يوجد رقم هاتف مسجّل" : "No phone number on file");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!student) return <Layout><div className="p-8 text-slate-500">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1400px] mx-auto">
        <Link to="/students" className="text-sm text-slate-500 hover:text-[#7A1B28] inline-flex items-center gap-1 mb-4"
              data-testid="back-to-students">
          <ArrowLeft size={14} /> {t("students")}
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="eyebrow">{t("student")}</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#0B192C] mt-1"
                data-testid="student-name">
              {student.name}
            </h1>
            <div className="text-slate-500 text-sm mt-1">
              {student.nationality || "—"} · {student.education_level || "—"}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setEditOpen(true)}
                    data-testid="edit-student-btn">
              <Pencil size={16} className="me-2" />
              {lang === "ar" ? "تعديل البيانات" : "Edit details"}
            </Button>
            <Button variant="outline" onClick={openWhatsApp}
                    disabled={!student.phone}
                    className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    data-testid="whatsapp-btn">
              <MessageCircle size={16} className="me-2" /> {t("whatsappMsg")}
            </Button>
            <span className={`inline-block text-xs px-2 py-1 rounded-md border ${STAGE_STYLES[student.stage] || ""}`}>
              {ts(student.stage)}
            </span>
            <Select value={student.stage} onValueChange={updateStage}>
              <SelectTrigger className="w-56" data-testid="student-stage-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {meta.stages.map((s) => <SelectItem key={s} value={s}>{ts(s)}</SelectItem>)}
              </SelectContent>
            </Select>
            {user?.role === "admin" && (
              <Button variant="outline" onClick={deleteStudent} disabled={deleting}
                      className="border-red-200 text-red-700 hover:bg-red-50"
                      data-testid="delete-student-btn">
                <Trash2 size={16} className="me-2" />
                {deleting
                  ? (lang === "ar" ? "جارِ الحذف…" : "Deleting…")
                  : (lang === "ar" ? "حذف الطالب" : "Delete student")}
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="p-5 border border-slate-200 shadow-none" data-testid="student-contact">
            <div className="eyebrow mb-3">{t("contact")}</div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-slate-700">
                <Phone size={14} className="text-slate-400" />{student.phone || "—"}
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <Mail size={14} className="text-slate-400" />{student.email || "—"}
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <IdCard size={14} className="text-slate-400" />
                <span data-testid="student-passport">{student.passport_number || "—"}</span>
              </div>
              <div className="flex items-start gap-2 text-slate-700">
                <Globe size={14} className="text-slate-400 mt-0.5" />
                <div className="flex flex-wrap gap-1">
                  {(student.target_countries || []).map((c) => (
                    <span key={c} className="px-2 py-0.5 text-xs rounded-md bg-slate-100">{tc(c)}</span>
                  ))}
                  {(!student.target_countries || student.target_countries.length === 0) && "—"}
                </div>
              </div>
            </div>
          </Card>

          <Card className="lg:col-span-2 p-0 border border-slate-200 shadow-none">
            <Tabs defaultValue="activity" className="w-full">
              <TabsList className="w-full justify-start rounded-none border-b border-slate-200 bg-transparent px-4">
                <TabsTrigger value="activity" data-testid="tab-activity">{t("activity")}</TabsTrigger>
                <TabsTrigger value="applications" data-testid="tab-applications">
                  {lang === "ar" ? "الطلبات" : "Applications"}
                </TabsTrigger>
                <TabsTrigger value="documents" data-testid="tab-documents">{t("documents")}</TabsTrigger>
                <TabsTrigger value="tasks" data-testid="tab-tasks">{t("tasks")}</TabsTrigger>
              </TabsList>

              <TabsContent value="activity" className="p-5">
                <NotesPanel studentId={id} notes={notes} reload={load} />
              </TabsContent>
              <TabsContent value="applications" className="p-5">
                <ApplicationsPanel studentId={id} apps={apps} meta={meta} reload={load} />
              </TabsContent>
              <TabsContent value="documents" className="p-5">
                <DocumentsPanel studentId={id} docs={docs} reload={load} />
              </TabsContent>
              <TabsContent value="tasks" className="p-5">
                <TasksInline studentId={id} tasks={tasks} reload={load} />
              </TabsContent>
            </Tabs>
          </Card>
        </div>

        <EditStudentDialog open={editOpen} onOpenChange={setEditOpen}
                           student={student} countries={meta.countries}
                           onSaved={(updated) => { setStudent(updated); setEditOpen(false); }} />
      </div>
    </Layout>
  );
}

function ApplicationsPanel({ studentId, apps, meta, reload }) {
  const { lang, tc } = useI18n();
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const blank = {
    university: "", program: "", country: "", intake: "",
    status: "Preparing", tuition_fee: "", commission: "",
    currency: "USD", notes: "",
  };
  const [form, setForm] = useState(blank);

  const STATUS_AR = {
    "Preparing": "قيد التجهيز",
    "Submitted": "تم التقديم",
    "Offer Received": "وصل القبول المبدئي",
    "Accepted": "مقبول",
    "Rejected": "مرفوض",
    "Enrolled": "مسجّل",
  };
  const STATUS_STYLE = {
    "Preparing": "bg-slate-50 text-slate-700 border-slate-200",
    "Submitted": "bg-blue-50 text-blue-700 border-blue-200",
    "Offer Received": "bg-purple-50 text-purple-700 border-purple-200",
    "Accepted": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Rejected": "bg-red-50 text-red-700 border-red-200",
    "Enrolled": "bg-teal-50 text-teal-700 border-teal-200",
  };
  const sName = (s) => (lang === "ar" ? STATUS_AR[s] || s : s);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/applications", {
        ...form,
        student_id: studentId,
        tuition_fee: parseFloat(form.tuition_fee) || 0,
        commission: parseFloat(form.commission) || 0,
      });
      setForm(blank);
      setAdding(false);
      toast.success(lang === "ar" ? "تمت إضافة الطلب" : "Application added");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const patch = async (aid, updates) => {
    try {
      await api.patch(`/applications/${aid}`, updates);
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (a) => {
    const msg = lang === "ar"
      ? `حذف طلب "${a.university}"؟`
      : `Delete the application to "${a.university}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/applications/${a.id}`);
      toast.success(lang === "ar" ? "تم الحذف" : "Deleted");
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Commission totals for this student, split by currency
  const totals = {};
  apps.forEach((a) => {
    const cur = a.currency || "USD";
    const b = totals[cur] || (totals[cur] = { expected: 0, received: 0 });
    b.expected += Number(a.commission) || 0;
    if (a.commission_received) b.received += Number(a.commission) || 0;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-wrap gap-3 text-sm">
          {Object.entries(totals).map(([cur, b]) => (
            <span key={cur} className="px-3 py-1.5 rounded-md bg-slate-50 border border-slate-200">
              <span className="text-slate-500">{lang === "ar" ? "العمولة" : "Commission"} ({cur}): </span>
              <span className="font-medium text-[#0B192C]">{b.received.toLocaleString()}</span>
              <span className="text-slate-400"> / {b.expected.toLocaleString()}</span>
            </span>
          ))}
        </div>
        <Button onClick={() => setAdding((v) => !v)}
                className="bg-[#7A1B28] hover:bg-[#601520] text-white"
                data-testid="add-application-btn">
          <GraduationCap size={16} className="me-2" />
          {adding
            ? (lang === "ar" ? "إلغاء" : "Cancel")
            : (lang === "ar" ? "إضافة طلب" : "Add application")}
        </Button>
      </div>

      {adding && (
        <form onSubmit={submit} className="space-y-3 p-4 rounded-lg border border-slate-200 bg-slate-50"
              data-testid="new-application-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{lang === "ar" ? "الجامعة" : "University"}</Label>
              <Input required value={form.university}
                     onChange={(e) => setForm({ ...form, university: e.target.value })}
                     data-testid="app-university" />
            </div>
            <div>
              <Label className="text-xs">{lang === "ar" ? "التخصص" : "Program"}</Label>
              <Input value={form.program}
                     onChange={(e) => setForm({ ...form, program: e.target.value })}
                     data-testid="app-program" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">{lang === "ar" ? "الدولة" : "Country"}</Label>
              <Select value={form.country || undefined}
                      onValueChange={(v) => setForm({ ...form, country: v })}>
                <SelectTrigger data-testid="app-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta.countries || []).map((c) => (
                    <SelectItem key={c} value={c}>{tc(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{lang === "ar" ? "الفصل الدراسي" : "Intake"}</Label>
              <Input value={form.intake} placeholder="Fall 2026"
                     onChange={(e) => setForm({ ...form, intake: e.target.value })}
                     data-testid="app-intake" />
            </div>
            <div>
              <Label className="text-xs">{lang === "ar" ? "الحالة" : "Status"}</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="app-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta.application_statuses || []).map((s) => (
                    <SelectItem key={s} value={s}>{sName(s)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">{lang === "ar" ? "الرسوم الدراسية" : "Tuition fee"}</Label>
              <Input type="number" step="any" min="0" value={form.tuition_fee}
                     onChange={(e) => setForm({ ...form, tuition_fee: e.target.value })}
                     data-testid="app-tuition" />
            </div>
            <div>
              <Label className="text-xs">{lang === "ar" ? "عمولتك" : "Your commission"}</Label>
              <Input type="number" step="any" min="0" value={form.commission}
                     onChange={(e) => setForm({ ...form, commission: e.target.value })}
                     data-testid="app-commission" />
            </div>
            <div>
              <Label className="text-xs">{lang === "ar" ? "العملة" : "Currency"}</Label>
              <Input value={form.currency}
                     onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                     data-testid="app-currency" />
            </div>
          </div>
          <div>
            <Label className="text-xs">{lang === "ar" ? "ملاحظات" : "Notes"}</Label>
            <Textarea rows={2} value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      data-testid="app-notes" />
          </div>
          <Button type="submit" disabled={saving}
                  className="bg-[#7A1B28] hover:bg-[#601520] text-white"
                  data-testid="app-save">
            {saving ? (lang === "ar" ? "جارِ الحفظ…" : "Saving…") : (lang === "ar" ? "حفظ" : "Save")}
          </Button>
        </form>
      )}

      <div className="space-y-3" data-testid="applications-list">
        {apps.length === 0 && (
          <div className="text-sm text-slate-500">
            {lang === "ar" ? "لا توجد طلبات بعد." : "No applications yet."}
          </div>
        )}
        {apps.map((a) => (
          <div key={a.id} className="p-4 rounded-lg border border-slate-200"
               data-testid={`application-${a.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-medium text-[#0B192C]">{a.university}</div>
                <div className="text-sm text-slate-600">
                  {[a.program, a.country ? tc(a.country) : "", a.intake].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={a.status} onValueChange={(v) => patch(a.id, { status: v })}>
                  <SelectTrigger className="w-44 h-8 text-xs" data-testid={`app-status-${a.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(meta.application_statuses || []).map((s) => (
                      <SelectItem key={s} value={s}>{sName(s)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => remove(a)}
                        data-testid={`app-delete-${a.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-4 flex-wrap text-sm">
              <span className={`inline-block text-xs px-2 py-1 rounded-md border ${STATUS_STYLE[a.status] || ""}`}>
                {sName(a.status)}
              </span>
              {Number(a.tuition_fee) > 0 && (
                <span className="text-slate-600">
                  {lang === "ar" ? "الرسوم" : "Tuition"}: {Number(a.tuition_fee).toLocaleString()} {a.currency}
                </span>
              )}
              {Number(a.commission) > 0 && (
                <button type="button"
                        onClick={() => patch(a.id, { commission_received: !a.commission_received })}
                        className={`inline-flex items-center gap-1.5 text-sm ${a.commission_received ? "text-emerald-700" : "text-slate-500 hover:text-slate-800"}`}
                        data-testid={`app-commission-toggle-${a.id}`}>
                  {a.commission_received ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                  {lang === "ar" ? "العمولة" : "Commission"}: {Number(a.commission).toLocaleString()} {a.currency}
                  <span className="text-xs">
                    ({a.commission_received
                      ? (lang === "ar" ? "مستلمة" : "received")
                      : (lang === "ar" ? "غير مستلمة" : "pending")})
                  </span>
                </button>
              )}
            </div>

            {a.notes && (
              <div className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">{a.notes}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function EditStudentDialog({ open, onOpenChange, student, countries, onSaved }) {
  const { t, tc, lang } = useI18n();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Refill the form each time the dialog opens so it always shows current data
  useEffect(() => {
    if (open && student) {
      setForm({
        name: student.name || "",
        phone: student.phone || "",
        email: student.email || "",
        nationality: student.nationality || "",
        education_level: student.education_level || "",
        passport_number: student.passport_number || "",
        target_countries: student.target_countries || [],
      });
    }
  }, [open, student]);

  const toggleCountry = (c) => {
    setForm((f) => ({
      ...f,
      target_countries: f.target_countries.includes(c)
        ? f.target_countries.filter((x) => x !== c)
        : [...f.target_countries, c],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/students/${student.id}`, form);
      toast.success(lang === "ar" ? "تم حفظ التعديلات" : "Changes saved");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!form) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-student-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#0B192C]">
            {lang === "ar" ? "تعديل بيانات الطالب" : "Edit student details"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>{t("name")}</Label>
            <Input required value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="edit-student-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("phone")}</Label>
              <Input value={form.phone}
                     onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     data-testid="edit-student-phone" />
            </div>
            <div>
              <Label>{t("email")}</Label>
              <Input type="email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     data-testid="edit-student-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("nationality")}</Label>
              <Input value={form.nationality}
                     onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                     data-testid="edit-student-nationality" />
            </div>
            <div>
              <Label>{t("educationLevel")}</Label>
              <Input value={form.education_level}
                     onChange={(e) => setForm({ ...form, education_level: e.target.value })}
                     data-testid="edit-student-education" />
            </div>
          </div>
          <div>
            <Label>{t("passportNumber")}</Label>
            <Input value={form.passport_number}
                   onChange={(e) => setForm({ ...form, passport_number: e.target.value })}
                   data-testid="edit-student-passport" />
          </div>
          <div>
            <Label>{t("targetCountries")}</Label>
            <div className="grid grid-cols-3 gap-2 mt-2 max-h-40 overflow-y-auto pe-1">
              {(countries || []).map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={form.target_countries.includes(c)}
                            onCheckedChange={() => toggleCountry(c)}
                            data-testid={`edit-country-${c}`} />
                  <span>{tc(c)}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}
                    className="bg-[#7A1B28] hover:bg-[#601520] text-white"
                    data-testid="edit-student-save">
              {saving ? (lang === "ar" ? "جارِ الحفظ…" : "Saving…") : t("save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NotesPanel({ studentId, notes, reload }) {
  const { t, tk } = useI18n();
  const [kind, setKind] = useState("note");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSaving(true);
    try {
      await api.post("/notes", { student_id: studentId, kind, content });
      setContent("");
      await reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <div>
      <form onSubmit={submit} className="space-y-3 mb-5" data-testid="note-form">
        <div className="flex gap-2 items-end">
          <div className="w-40">
            <Label className="text-xs">{t("kind")}</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="note-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="call">{t("call")}</SelectItem>
                <SelectItem value="meeting">{t("meeting")}</SelectItem>
                <SelectItem value="follow-up">{t("followup")}</SelectItem>
                <SelectItem value="note">{t("note")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Textarea value={content} rows={2} onChange={(e) => setContent(e.target.value)}
                      placeholder={t("addNote")} data-testid="note-content" />
          </div>
          <Button type="submit" disabled={saving}
                  className="bg-[#7A1B28] hover:bg-[#601520] text-white"
                  data-testid="note-save">
            {t("save")}
          </Button>
        </div>
      </form>
      <div className="space-y-3" data-testid="notes-list">
        {notes.length === 0 && <div className="text-sm text-slate-500">{t("empty")}</div>}
        {notes.map((n) => (
          <div key={n.id} className="border-s-2 border-[#7A1B28] ps-3">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="uppercase tracking-wider font-medium">{tk(n.kind)}</span> ·
              <span>{new Date(n.created_at).toLocaleString()}</span> ·
              <span>{n.author_name}</span>
            </div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.content}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DocumentsPanel({ studentId, docs, reload }) {
  const { t } = useI18n();
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("passport");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/students/${studentId}/documents?doc_type=${docType}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null);
      toast.success(t("upload"));
      await reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const openDoc = (doc) => {
    const token = localStorage.getItem("mer_token");
    window.open(`${API}/documents/${doc.id}/download?auth=${encodeURIComponent(token)}`, "_blank");
  };

  const remove = async (id) => {
    try {
      await api.delete(`/documents/${id}`);
      await reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <form onSubmit={submit} className="flex gap-2 items-end mb-5 flex-wrap" data-testid="doc-form">
        <div className="w-44">
          <Label className="text-xs">{t("docType")}</Label>
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="passport">{t("passport")}</SelectItem>
              <SelectItem value="transcripts">{t("transcripts")}</SelectItem>
              <SelectItem value="certificate">{t("certificate")}</SelectItem>
              <SelectItem value="other">{t("other")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs">{t("uploadDocument")}</Label>
          <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
                 data-testid="doc-file" />
        </div>
        <Button type="submit" disabled={!file || busy}
                className="bg-[#7A1B28] hover:bg-[#601520] text-white"
                data-testid="doc-upload">
          <Upload size={14} className="me-2" /> {t("upload")}
        </Button>
      </form>
      <div className="divide-y divide-slate-100" data-testid="docs-list">
        {docs.length === 0 && <div className="text-sm text-slate-500">{t("empty")}</div>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <FileText size={16} className="text-[#7A1B28]" />
              <div>
                <div className="text-sm font-medium text-[#0B192C]">{d.original_filename}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">{d.doc_type}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openDoc(d)} data-testid={`doc-open-${d.id}`}>
                {t("downloadOrView")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => remove(d.id)} data-testid={`doc-delete-${d.id}`}>
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TasksInline({ studentId, tasks, reload }) {
  const { t } = useI18n();
  const [title, setTitle] = useState("");
  const [due, setDue] = useState(new Date().toISOString().slice(0, 10));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await api.post("/tasks", { student_id: studentId, title, due_date: due });
      setTitle("");
      await reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggle = async (task) => {
    await api.patch(`/tasks/${task.id}`, { done: !task.done });
    await reload();
  };

  return (
    <div>
      <form onSubmit={submit} className="flex gap-2 items-end mb-5" data-testid="student-task-form">
        <div className="flex-1">
          <Label className="text-xs">{t("title")}</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder={t("addTask")} data-testid="student-task-title" />
        </div>
        <div className="w-40">
          <Label className="text-xs">{t("dueDate")}</Label>
          <Input type="date" value={due} onChange={(e) => setDue(e.target.value)}
                 data-testid="student-task-due" />
        </div>
        <Button type="submit" className="bg-[#7A1B28] hover:bg-[#601520] text-white"
                data-testid="student-task-save">{t("save")}</Button>
      </form>
      <div className="space-y-2" data-testid="student-tasks-list">
        {tasks.length === 0 && <div className="text-sm text-slate-500">{t("empty")}</div>}
        {tasks.map((tk) => (
          <label key={tk.id} className="flex items-center gap-3 p-2 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
            <input type="checkbox" checked={tk.done} onChange={() => toggle(tk)}
                   data-testid={`student-task-toggle-${tk.id}`} />
            <div className="flex-1">
              <div className={`text-sm ${tk.done ? "line-through text-slate-400" : "text-slate-800"}`}>{tk.title}</div>
              <div className="text-xs text-slate-500">{t("dueDate")}: {tk.due_date}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}
